import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  ChevronDown,
  Gift,
  Heart,
  MapPin,
  Package,
  ShieldCheck,
  ShoppingCart,
  Star,
  Truck,
  X,
  XCircle,
} from "lucide-react";
import "./ProductPurchaseCard.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../context/AuthContext";
import { useCart } from "../../../../context/CartContext";
import { useWishlist } from "../../../../context/WishlistContext";
import { ProductType } from "../../../../Redux/custmoer_cart_service/types";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { ADDRESS_SETUP_EVENT } from "../../../globalcomponents/LocationSetupModal/LocationSetupData";
import { checkPdpDelivery } from "../../../../Redux/shipping-apis/slice";

const PRODUCT_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || ""}${process.env.REACT_APP_ECOMMERCE_PERFIX || ""}`;

type ProductPurchaseCardProps = {
  productId?: string | number;
  productTitle?: string;
  productImage?: string;
  productDescription?: string;
  price: number;
  mrp?: number;
  variantId?: string;
  variantName?: string;
  sellerName?: string;
  sellerSlug?: string;
  sellerId?: string;
  stock?: boolean;
  freeDelivery?: boolean;
  fastDelivery?: boolean;
  nextDayDelivery?: boolean;
  shipsFrom?: string;
  packaging?: string;
  payment?: string;
  giftOptions?: string;
  category?: string;
  returnPolicy?: string;
  warranty?: string;
  otherOffers?: any[];
  sellerCount?: number;

  onAddToCart?: (quantity: number) => void;

  onBuyNow?: (quantity: number) => void;

  onWishlist?: () => void;
};

const formatPrice = (value: number) =>
  new Intl.NumberFormat("en-IN").format(value);

const ProductPurchaseCard = ({
  productId,
  productTitle,
  productImage,
  productDescription,
  price,
  mrp,
  variantId,
  variantName,
  sellerName = "-",
  sellerSlug,
  sellerId,
  stock = true,
  freeDelivery = false,
  fastDelivery = false,
  nextDayDelivery = false,
  shipsFrom,
  packaging,
  payment,
  giftOptions,
  category,
  returnPolicy,
  warranty,
  otherOffers,
  sellerCount,
  onAddToCart,
  onBuyNow,
  onWishlist,
}: ProductPurchaseCardProps) => {
  const navigate = useNavigate();

  const rawSeller =
    typeof sellerName === "string" ? sellerName : (sellerName as any)?.name;

  const resolvedSellerName =
    typeof rawSeller === "string" && rawSeller.trim() ? rawSeller.trim() : "-";

  const rawSellerSlug =
    typeof sellerSlug === "string" ? sellerSlug.trim() : undefined;

  const resolvedSellerSlug =
    rawSellerSlug ||
    (resolvedSellerName !== "-"
      ? resolvedSellerName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "")
      : "");

  const resolvedShipsFrom =
    typeof shipsFrom === "string" && shipsFrom.trim() ? shipsFrom.trim() : "-";

  const resolvedPackaging =
    typeof packaging === "string" && packaging.trim() ? packaging.trim() : "-";

  const resolvedPayment =
    typeof payment === "string" && payment.trim() ? payment.trim() : "-";

  const resolvedGiftOptions =
    typeof giftOptions === "string" && giftOptions.trim() ? giftOptions.trim() : "-";

  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const { location } = useAppSelector((state) => state.deliveryLocation);
  const { addresses } = useAppSelector((state) => state.customerAddress);
  const {
    pdpDelivery,
    pdpDeliveryLoading,
    error: shippingError,
  } = useAppSelector((state) => state.shipping);

  const isFast = Boolean(fastDelivery || nextDayDelivery);

  const deliveryDateInfo = useMemo(() => {
    const now = new Date();
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const weekdays = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];

    if (isFast) {
      const tomorrow = new Date(now);
      tomorrow.setDate(now.getDate() + 1);
      return `Tomorrow, ${tomorrow.getDate()} ${months[tomorrow.getMonth()]}`;
    }

    const stdDate = new Date(now);
    stdDate.setDate(now.getDate() + 4);
    return `${weekdays[stdDate.getDay()]}, ${stdDate.getDate()} ${months[stdDate.getMonth()]}`;
  }, [isFast]);

  const orderWithinInfo = useMemo(() => {
    const now = new Date();
    const endOfDay = new Date(now);
    endOfDay.setHours(23, 59, 59, 999);
    const diffMs = endOfDay.getTime() - now.getTime();
    const hrs = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60)));
    const mins = Math.max(
      0,
      Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60)),
    );
    return `${hrs} hrs and ${mins} mins`;
  }, []);

  const benefitsList = useMemo(() => {
    const securityTitle =
      payment && payment !== "-" ? payment : "Secure checkout";
    const securityDesc =
      payment && payment !== "-" && payment !== "Secure transaction"
        ? `Protected checkout with verified ${payment.toLowerCase()} options.`
        : "Payments are processed securely through 256-bit protected checkout.";

    const catStr = (
      (typeof category === "string" ? category : "") +
      " " +
      (typeof productTitle === "string" ? productTitle : "")
    ).toLowerCase();

    let supportTitle = "Easy support";
    let supportDesc =
      returnPolicy ||
      warranty ||
      "Returns and replacement options available on eligible orders.";

    if (warranty) {
      supportTitle = "Brand Warranty";
      supportDesc = warranty;
    } else if (returnPolicy) {
      supportTitle = "Return & Replacement";
      supportDesc = returnPolicy;
    } else if (
      catStr.includes("electronic") ||
      catStr.includes("mobile") ||
      catStr.includes("phone") ||
      catStr.includes("laptop") ||
      catStr.includes("tablet")
    ) {
      supportTitle = "7 Days Replacement";
      supportDesc =
        "7-day replacement for hardware defects & brand warranty applies.";
    } else if (
      catStr.includes("fashion") ||
      catStr.includes("men") ||
      catStr.includes("women") ||
      catStr.includes("cloth") ||
      catStr.includes("shoe")
    ) {
      supportTitle = "7 Days Return";
      supportDesc =
        "Hassle-free 7-day return and exchange available for size and fit.";
    } else if (
      catStr.includes("grocer") ||
      catStr.includes("food") ||
      catStr.includes("fresh")
    ) {
      supportTitle = "Quality Guarantee";
      supportDesc =
        "Non-returnable. Replacement or refund for damaged or expired items.";
    } else if (
      catStr.includes("home") ||
      catStr.includes("appliance") ||
      catStr.includes("furniture")
    ) {
      supportTitle = "10 Days Replacement";
      supportDesc =
        "Free replacement in case of transit damage or manufacturing defect.";
    }

    let thirdIcon: "gift" | "truck" | "package" = "gift";
    let thirdTitle = "Gift-ready options";
    let thirdDesc = "Add eligible gift preferences during checkout.";

    const hasGift =
      giftOptions &&
      giftOptions !== "-" &&
      !giftOptions.toLowerCase().includes("not available");

    if (hasGift) {
      thirdIcon = "gift";
      thirdTitle = "Gift-ready options";
      thirdDesc =
        giftOptions !== "Available at checkout"
          ? giftOptions
          : "Add eligible gift preferences during checkout.";
    } else if (fastDelivery || nextDayDelivery) {
      thirdIcon = "truck";
      thirdTitle = "Fast Delivery";
      thirdDesc = "Priority dispatch and expedited delivery available.";
    } else if (freeDelivery) {
      thirdIcon = "truck";
      thirdTitle = "Free Delivery";
      thirdDesc = "Free shipping on this order with doorstep delivery.";
    } else if (shipsFrom && shipsFrom !== "-") {
      thirdIcon = "package";
      thirdTitle = "Direct shipping";
      thirdDesc = `Ships from ${shipsFrom} with end-to-end transit protection.`;
    } else {
      thirdIcon = "truck";
      thirdTitle = "Tracked Delivery";
      thirdDesc = "Reliable doorstep delivery with live tracking.";
    }

    return [
      {
        icon: "shield" as const,
        title: securityTitle,
        description: securityDesc,
      },
      {
        icon: "package" as const,
        title: supportTitle,
        description: supportDesc,
      },
      {
        icon: thirdIcon,
        title: thirdTitle,
        description: thirdDesc,
      },
    ];
  }, [
    payment,
    category,
    productTitle,
    returnPolicy,
    warranty,
    giftOptions,
    fastDelivery,
    nextDayDelivery,
    freeDelivery,
    shipsFrom,
  ]);

  const activeAddress =
    addresses?.find((a) => a.defaultAddress) ||
    (addresses && addresses.length > 0 ? addresses[0] : null);

  const [pincode, setPincode] = useState("");
  const [pincodeTouched, setPincodeTouched] = useState(false);
  const didPrefillRef = useRef(false);
  const lastFetchedKeyRef = useRef<string>("");
  const checkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const savedPincode = location?.pincode || activeAddress?.pincode || "";

  // Prefill navbar delivery location once (Amazon-style)
  useEffect(() => {
    if (didPrefillRef.current) return;
    const initialPin = savedPincode.replace(/\D/g, "").slice(0, 6);
    if (initialPin && initialPin.length === 6) {
      didPrefillRef.current = true;
      setPincode(initialPin);
    }
  }, [savedPincode]);

  // Auto-check when sellerId and a 6-digit pincode are available (debounced, deduped)
  useEffect(() => {
    if (checkTimerRef.current) {
      clearTimeout(checkTimerRef.current);
      checkTimerRef.current = null;
    }

    if (!sellerId || pincode.length !== 6) return;

    const key = `${sellerId}:${pincode}`;
    if (lastFetchedKeyRef.current === key) return;

    checkTimerRef.current = setTimeout(() => {
      lastFetchedKeyRef.current = key;
      dispatch(checkPdpDelivery({ sellerId, customerPincode: pincode }));
    }, 350);

    return () => {
      if (checkTimerRef.current) {
        clearTimeout(checkTimerRef.current);
        checkTimerRef.current = null;
      }
    };
  }, [dispatch, sellerId, pincode]);

  const handlePincodeCheck = () => {
    setPincodeTouched(true);
    const value = pincode.trim();
    if (value.length !== 6) {
      return;
    }

    if (sellerId) {
      const key = `${sellerId}:${value}`;
      lastFetchedKeyRef.current = key;
      dispatch(checkPdpDelivery({ sellerId, customerPincode: value }));
    }
  };

  const hasChecked = Boolean(
    pdpDelivery &&
    pdpDelivery.customerPincode === pincode &&
    pincode.length === 6
  );

  const isDeliverable = Boolean(
    hasChecked &&
    (pdpDelivery?.delhivery?.serviceable === true ||
      pdpDelivery?.delhivery?.customerServiceable === true ||
      pdpDelivery?.deliveryOptions?.some((o) => o.available) ||
      pdpDelivery?.hyperlocal?.serviceable === true)
  );

  const isUndeliverable = Boolean(
    (hasChecked && !isDeliverable) || (shippingError && pincodeTouched && pincode.length === 6)
  );

  const effectivePincode = pincode || savedPincode;
  const effectiveName =
    user?.name || user?.fullName || activeAddress?.fullName || "";
  const effectiveCity =
    location?.city || location?.area || activeAddress?.city || "";

  const deliveryAddressLabel = useMemo(() => {
    if (effectiveName && effectiveCity) {
      return `Deliver to ${effectiveName} - ${effectiveCity} ${effectivePincode}`.trim();
    }
    if (effectiveCity) {
      return `Deliver to ${effectiveCity} ${effectivePincode ? `- ${effectivePincode}` : ""}`.trim();
    }
    if (effectivePincode) {
      return `Deliver to Location - ${effectivePincode}`;
    }
    return "Deliver to Select delivery location";
  }, [effectiveName, effectiveCity, effectivePincode]);

  const [quantity, setQuantity] = useState(1);
  const [quantityMenuOpen, setQuantityMenuOpen] = useState(false);

  const handleAddressClick = () => {
    window.dispatchEvent(new CustomEvent(ADDRESS_SETUP_EVENT));
  };

  const { addToCart, setCartOpen, isProductInCart, cartItems } = useCart();
  const { toggleWishlist, isItemWishlisted } = useWishlist();

  const effectiveProductId = String(productId || "");

  const inCart = useMemo(() => {
    if (!effectiveProductId) return false;
    const foundInList = (cartItems || []).some((item: any) => {
      const itemProdId = String(item.productId || item._id || item.id || "");
      if (itemProdId !== effectiveProductId) return false;
      if (variantId && item.variantId && item.variantId !== "default") {
        return String(item.variantId) === String(variantId);
      }
      return true;
    });
    return foundInList || isProductInCart(effectiveProductId, variantId);
  }, [effectiveProductId, variantId, cartItems, isProductInCart]);

  const isWishlisted = Boolean(
    effectiveProductId && isItemWishlisted(effectiveProductId)
  );

  const handleAddToCart = async () => {
    if (!stock) {
      return;
    }

    if (inCart) {
      setCartOpen(true);
      return;
    }

    if (onAddToCart) {
      await onAddToCart(quantity);
      setCartOpen(true);
      return;
    }

    if (effectiveProductId) {
      await addToCart({
        productId: effectiveProductId,
        productName: productTitle || "Product",
        productImage: productImage,
        productType: ProductType.PHYSICAL,
        variantId: variantId,
        variantName: variantName,
        quantity: quantity,
        unitPrice: price,
        price: price,
        mrp: mrp || price,
        sellerId: sellerId,
        sellerName: resolvedSellerName !== "-" ? resolvedSellerName : undefined,
        isMLMProduct: false,
        metadata: {
          mrp: mrp || price,
          image: productImage,
          description: productDescription,
        },
      });
      setCartOpen(true);
    }
  };

  const handleBuyNow = async () => {
    if (!stock) {
      return;
    }

    if (onBuyNow) {
      onBuyNow(quantity);
      return;
    }

    if (effectiveProductId) {
      await addToCart({
        productId: effectiveProductId,
        productName: productTitle || "Product",
        productImage: productImage,
        productType: ProductType.PHYSICAL,
        variantId: variantId,
        variantName: variantName,
        quantity: quantity,
        unitPrice: price,
        price: price,
        mrp: mrp || price,
        sellerId: sellerId,
        sellerName: resolvedSellerName !== "-" ? resolvedSellerName : undefined,
        isMLMProduct: false,
        metadata: {
          mrp: mrp || price,
          image: productImage,
          description: productDescription,
        },
      });
      navigate("/Cart");
    }
  };

  const handleWishlistToggle = () => {
    if (onWishlist) {
      onWishlist();
    } else if (effectiveProductId) {
      toggleWishlist(effectiveProductId, {
        productVariantId: variantId,
      });
    }
  };

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerOffers, setDrawerOffers] = useState<any[] | null>(null);
  const [loadingDrawerOffers, setLoadingDrawerOffers] = useState(false);
  const [addingOfferId, setAddingOfferId] = useState<string | null>(null);

  // Compute effective other seller offers from prop or lazy fetch
  const effectiveOtherOffers = useMemo(() => {
    const list = drawerOffers !== null ? drawerOffers : (otherOffers || []);
    return list.filter((o: any) => {
      if (!o) return false;
      if (sellerId && o.sellerId && String(o.sellerId) === String(sellerId)) {
        return false;
      }
      return true;
    });
  }, [drawerOffers, otherOffers, sellerId]);

  const otherSellersCount = effectiveOtherOffers.length;

  const minOtherPrice = useMemo(() => {
    if (!effectiveOtherOffers.length) return price;
    const validPrices = effectiveOtherOffers
      .map((o: any) =>
        Number(o.finalPrice ?? o.displayPrice ?? o.sellerPrice ?? o.unitPrice ?? 0)
      )
      .filter((p: number) => !isNaN(p) && p > 0);
    return validPrices.length > 0 ? Math.min(...validPrices) : price;
  }, [effectiveOtherOffers, price]);

  const handleOpenDrawer = async () => {
    setIsDrawerOpen(true);
    if (drawerOffers === null && effectiveProductId) {
      setLoadingDrawerOffers(true);
      try {
        const url = `${PRODUCT_BASE_URL}/e-commerce/catalog/products/${effectiveProductId}/sellers${
          variantId ? `?variantId=${variantId}` : ""
        }`;
        const res = await axios.get(url);
        const sellers = res.data?.data?.sellers;
        if (Array.isArray(sellers)) {
          const featSku = res.data?.data?.featuredOffer?.sellerSkuId;
          const featSellerId = res.data?.data?.featuredOffer?.sellerId || sellerId;
          const filtered = sellers.filter((s: any) => {
            if (featSku && s.sellerSkuId === featSku) return false;
            if (featSellerId && s.sellerId === featSellerId) return false;
            return true;
          });
          setDrawerOffers(filtered);
        } else {
          setDrawerOffers(otherOffers || []);
        }
      } catch (e) {
        console.error("Failed to load seller offers:", e);
        setDrawerOffers(otherOffers || []);
      } finally {
        setLoadingDrawerOffers(false);
      }
    }
  };

  const handleAddOtherSellerToCart = async (offer: any) => {
    if (!effectiveProductId) return;
    const offerPrice = Number(
      offer.finalPrice ?? offer.displayPrice ?? offer.sellerPrice ?? offer.unitPrice ?? price
    );
    const offerMrp = Number(offer.mrp ?? mrp ?? offerPrice);
    const offerSellerId = offer.sellerId || sellerId;
    const offerSellerName =
      offer.sellerName && offer.sellerName.trim()
        ? offer.sellerName.trim()
        : offerSellerId
        ? `Seller ${String(offerSellerId).slice(-4)}`
        : "Marketplace Seller";

    const offerKey = String(offer.sellerSkuId || offer.sellerId || offerPrice);
    setAddingOfferId(offerKey);
    try {
      await addToCart({
        productId: effectiveProductId,
        productName: productTitle || "Product",
        productImage: productImage,
        productType: ProductType.PHYSICAL,
        variantId: variantId,
        variantName: variantName,
        quantity: 1,
        unitPrice: offerPrice,
        price: offerPrice,
        mrp: offerMrp,
        sellerId: offerSellerId,
        sellerSkuId: offer.sellerSkuId,
        sellerName: offerSellerName,
        isMLMProduct: false,
        metadata: {
          mrp: offerMrp,
          image: productImage,
          description: productDescription,
          sellerSkuId: offer.sellerSkuId,
        },
      });
      setIsDrawerOpen(false);
      setCartOpen(true);
    } catch (err) {
      console.error("Failed to add other seller offer to cart:", err);
    } finally {
      setAddingOfferId(null);
    }
  };

  useEffect(() => {
    if (!isDrawerOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsDrawerOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isDrawerOpen]);

  return (
    <div className="purchase-card-wrapper">
      <aside className="purchase-card">
        <div className="purchase-price">
          <sup>₹</sup>

          <strong>{formatPrice(price)}</strong>
        </div>

        <div className="purchase-delivery-message">
          <div className="purchase-delivery-title">
            <Truck size={18} strokeWidth={1.9} />

            <p>
              {stock ? (
                freeDelivery ? (
                  <>
                    FREE delivery <strong>{deliveryDateInfo}</strong>. Order
                    within <strong>{orderWithinInfo}</strong>.
                  </>
                ) : isFast ? (
                  <>
                    Fast delivery <strong>{deliveryDateInfo}</strong>. Order
                    within <strong>{orderWithinInfo}</strong>.
                  </>
                ) : (
                  <>
                    Delivery by <strong>{deliveryDateInfo}</strong>. Order
                    within <strong>{orderWithinInfo}</strong>.
                  </>
                )
              ) : (
                <>Delivery details available when back in stock.</>
              )}
            </p>
          </div>
        </div>

        <button
          type="button"
          className="purchase-address-link"
          onClick={handleAddressClick}
        >
          <MapPin size={17} strokeWidth={2} />

          <span>{deliveryAddressLabel}</span>
        </button>

        <div
          className={`purchase-stock ${
            !stock
              ? "unavailable"
              : isUndeliverable
              ? "unavailable"
              : "available"
          }`}
        >
          {!stock
            ? "Currently unavailable"
            : isUndeliverable
            ? "Currently unavailable for this location"
            : "In stock"}
        </div>

        <div className="purchase-mobile-controls">
          <div className="purchase-pincode">
            <span className="purchase-label">Check delivery availability</span>

            <div className="purchase-pincode-input">
              <MapPin size={17} strokeWidth={1.9} />

              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={pincode}
                onChange={(event) => {
                  const nextVal = event.target.value.replace(/\D/g, "");
                  setPincode(nextVal);
                  setPincodeTouched(false);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handlePincodeCheck();
                  }
                }}
                placeholder="Enter 6-digit pincode"
              />

              <button
                type="button"
                onClick={handlePincodeCheck}
                disabled={pincode.length !== 6 || pdpDeliveryLoading}
              >
                {pdpDeliveryLoading ? "Checking…" : "Check"}
              </button>
            </div>

            {pincodeTouched && pincode.length !== 6 && (
              <span className="purchase-pincode-error">
                <AlertCircle size={15} strokeWidth={2} />
                Please enter a valid 6-digit pincode
              </span>
            )}

            {pdpDeliveryLoading && (
              <span className="purchase-pincode-loading">
                Checking delivery options for {pincode}…
              </span>
            )}

            {!pdpDeliveryLoading && isDeliverable && (
              <span className="purchase-pincode-success">
                <CheckCircle2 size={15} strokeWidth={2} />
                {pdpDelivery?.deliveryCity
                  ? `Delivery available for ${pdpDelivery.deliveryCity} (${pincode})`
                  : `Delivery available for ${pincode}`}
              </span>
            )}

            {!pdpDeliveryLoading && isUndeliverable && (
              <span className="purchase-pincode-error">
                <XCircle size={15} strokeWidth={2} />
                {shippingError || `Delivery not available for ${pincode}`}
              </span>
            )}

            {!sellerId && pincodeTouched && (
              <span className="purchase-pincode-error">
                <AlertCircle size={15} strokeWidth={2} />
                Seller information is not available for this product
              </span>
            )}
          </div>

          <div className="purchase-divider purchase-controls-divider" />

          <div className="purchase-quantity">
            <label htmlFor="purchase-quantity">Quantity</label>

            <div
              className="purchase-quantity-select"
              onBlur={(event) => {
                const nextTarget =
                  event.relatedTarget as
                    | Node
                    | null;

                if (
                  nextTarget &&
                  event.currentTarget.contains(
                    nextTarget,
                  )
                ) {
                  return;
                }

                setQuantityMenuOpen(false);
              }}
            >
              <select
                id="purchase-quantity"
                value={quantity}
                onChange={(event) =>
                  setQuantity(
                    Number(
                      event.target.value,
                    ),
                  )
                }
              >
                {[1, 2, 3, 4, 5].map(
                  (item) => (
                    <option
                      key={item}
                      value={item}
                    >
                      {item}
                    </option>
                  ),
                )}
              </select>

              <ChevronDown
                className="purchase-quantity-desktop-chevron"
                size={16}
                strokeWidth={2}
              />

              <button
                type="button"
                className={`purchase-mobile-quantity-trigger ${quantityMenuOpen
                    ? "open"
                    : ""
                  }`}
                onClick={() =>
                  setQuantityMenuOpen(
                    (
                      current,
                    ) =>
                      !current,
                  )
                }
                aria-haspopup="listbox"
                aria-expanded={
                  quantityMenuOpen
                }
              >
                <span>{quantity}</span>

                <ChevronDown
                  size={16}
                  strokeWidth={2.1}
                />
              </button>

              {quantityMenuOpen && (
                <div
                  className="purchase-mobile-quantity-menu"
                  role="listbox"
                  aria-label="Select quantity"
                >
                  {[1, 2, 3, 4, 5].map(
                    (
                      item,
                    ) => (
                      <button
                        type="button"
                        key={`mobile-quantity-${item}`}
                        className={
                          quantity ===
                            item
                            ? "active"
                            : ""
                        }
                        onClick={() => {
                          setQuantity(
                            item,
                          );
                          setQuantityMenuOpen(
                            false,
                          );
                        }}
                        role="option"
                        aria-selected={
                          quantity ===
                          item
                        }
                      >
                        Qty {item}
                      </button>
                    ),
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="purchase-actions">
          <button
            type="button"
            className="purchase-cart-btn"
            disabled={!stock}
            onClick={handleAddToCart}
          >
            {inCart ? "Added to Cart" : "Add to Cart"}
          </button>

          
        </div>

        <div className="purchase-divider" />

        <div className="purchase-info-list">
          <div>
            <span>Ships from</span>

            <strong>{resolvedShipsFrom}</strong>
          </div>

          <div className="purchase-seller-row">
            <span>Sold by</span>

            {resolvedSellerName !== "-" ? (
              <button
                type="button"
                className="purchase-seller-link"
                aria-label={`View ${resolvedSellerName} seller details`}
                onClick={() => navigate(`/seller/${resolvedSellerSlug}`)}
              >
                <strong>{resolvedSellerName}</strong>

                <ShieldCheck size={14} strokeWidth={1.9} />
              </button>
            ) : (
              <strong>-</strong>
            )}
          </div>
          <div>
            <span>Packaging</span>

            <strong>{resolvedPackaging}</strong>
          </div>

          <div>
            <span>Payment</span>

            <strong>{resolvedPayment}</strong>
          </div>

          <div>
            <span>Gift options</span>

            <strong>{resolvedGiftOptions}</strong>
          </div>
        </div>

        <div className="purchase-divider" />

        <div className="purchase-benefits">
          {benefitsList.map((benefit, index) => (
            <div className="purchase-benefit-item" key={index}>
              <span className="purchase-benefit-icon">
                {benefit.icon === "shield" && (
                  <ShieldCheck size={19} strokeWidth={1.8} />
                )}
                {benefit.icon === "package" && (
                  <Package size={19} strokeWidth={1.8} />
                )}
                {benefit.icon === "gift" && (
                  <Gift size={19} strokeWidth={1.8} />
                )}
                {benefit.icon === "truck" && (
                  <Truck size={19} strokeWidth={1.8} />
                )}
              </span>

              <div>
                <strong>{benefit.title}</strong>

                <span>{benefit.description}</span>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          className="purchase-wishlist-btn"
          onClick={handleWishlistToggle}
          style={
            isWishlisted
              ? { borderColor: "#c9364a", background: "#fff5f6", color: "#c9364a" }
              : undefined
          }
        >
          <Heart
            size={17}
            strokeWidth={1.9}
            fill={isWishlisted ? "#c9364a" : "none"}
            color={isWishlisted ? "#c9364a" : "currentColor"}
          />
          {isWishlisted ? "In Wishlist" : "Add to Wishlist"}
        </button>
      </aside>

      <div className="purchase-other-sellers">
        <div className="purchase-other-sellers-head">
          <strong>More buying options</strong>
        </div>

        {otherSellersCount > 0 ? (
          <button
            type="button"
            className="purchase-other-sellers-btn"
            onClick={handleOpenDrawer}
            aria-label="View other buying options"
          >
            <span>
              <strong>
                {otherSellersCount} additional {otherSellersCount === 1 ? "seller" : "sellers"}
              </strong>

              <small>
                Starting from ₹{formatPrice(minOtherPrice > 0 ? minOtherPrice : price)}
              </small>
            </span>

            <ChevronRight size={19} strokeWidth={1.9} />
          </button>
        ) : (
          <div className="purchase-other-sellers-empty">
            <span>No additional sellers available</span>
            <small>
              {resolvedSellerName !== "-"
                ? `Currently offered exclusively by ${resolvedSellerName}`
                : "No alternative sellers for this product"}
            </small>
          </div>
        )}
      </div>

      {/* Slide-over Drawer for All Buying Options */}
      {isDrawerOpen && (
        <div className="purchase-sellers-modal-root" role="dialog" aria-modal="true">
          <div
            className="purchase-sellers-backdrop"
            onClick={() => setIsDrawerOpen(false)}
          />
          <aside className="purchase-sellers-drawer">
            <div className="purchase-drawer-header">
              <div>
                <h3>All Buying Options</h3>
                <p title={productTitle}>{productTitle || "Product Offers"}</p>
              </div>
              <button
                type="button"
                className="purchase-drawer-close-btn"
                onClick={() => setIsDrawerOpen(false)}
                aria-label="Close buying options"
              >
                <X size={20} />
              </button>
            </div>

            <div className="purchase-drawer-body">
              {/* Featured / Current Offer Card */}
              <div className="purchase-offer-card current-selection">
                <div className="purchase-offer-badge">Current Selection</div>
                <div className="purchase-offer-top">
                  <div className="purchase-offer-pricing">
                    <span className="purchase-offer-price">₹{formatPrice(price)}</span>
                    {mrp && mrp > price && (
                      <span className="purchase-offer-mrp">₹{formatPrice(mrp)}</span>
                    )}
                  </div>
                  <div className={`purchase-offer-stock ${stock ? "in-stock" : "out-of-stock"}`}>
                    {stock ? "In Stock" : "Out of Stock"}
                  </div>
                </div>

                <div className="purchase-offer-seller-info">
                  <span>Sold by:</span>
                  <strong>{resolvedSellerName}</strong>
                </div>

                <div className="purchase-offer-delivery-info">
                  <Truck size={15} />
                  <span>
                    {isFast
                      ? `Fast Delivery (${deliveryDateInfo})`
                      : freeDelivery
                      ? "Free Delivery"
                      : "Standard Delivery"}
                  </span>
                </div>
                {resolvedShipsFrom !== "-" && (
                  <div className="purchase-offer-ships">
                    <span>Ships from:</span>
                    <strong>{resolvedShipsFrom}</strong>
                  </div>
                )}
              </div>

              {/* Other Sellers Section */}
              <div className="purchase-drawer-section-title">
                <strong>Other Offers ({effectiveOtherOffers.length})</strong>
              </div>

              {loadingDrawerOffers ? (
                <div className="purchase-drawer-loading">
                  <div className="purchase-spinner" />
                  <span>Loading seller offers...</span>
                </div>
              ) : effectiveOtherOffers.length === 0 ? (
                <div className="purchase-drawer-empty">
                  <span>No other sellers are offering this item right now.</span>
                </div>
              ) : (
                <div className="purchase-offers-list">
                  {effectiveOtherOffers.map((offer: any, idx: number) => {
                    const offerPrice = Number(
                      offer.finalPrice ?? offer.displayPrice ?? offer.sellerPrice ?? offer.unitPrice ?? price
                    );
                    const offerMrp = Number(offer.mrp ?? mrp ?? offerPrice);
                    const offerName =
                      offer.sellerName && offer.sellerName.trim()
                        ? offer.sellerName.trim()
                        : offer.sellerId
                        ? `Seller ${String(offer.sellerId).slice(-4)}`
                        : "Marketplace Seller";
                    const offerSlug =
                      offer.sellerSlug ||
                      (offerName && offerName !== "Marketplace Seller"
                        ? offerName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
                        : undefined);
                    const isOfferInStock =
                      offer.stockStatus !== "out_of_stock" &&
                      (offer.availableQty === undefined || offer.availableQty > 0);
                    const hasFreeDelivery = Boolean(
                      offer.delivery?.freeDelivery ?? offer.freeDelivery
                    );
                    const hasFastDelivery = Boolean(
                      offer.delivery?.fastDelivery ?? offer.fastDelivery
                    );
                    const rating = Number(offer.sellerRating || 0);
                    const offerKey = String(offer.sellerSkuId || offer.sellerId || idx);
                    const isAdding = addingOfferId === offerKey;

                    return (
                      <div className="purchase-offer-card" key={offerKey}>
                        <div className="purchase-offer-top">
                          <div className="purchase-offer-pricing">
                            <span className="purchase-offer-price">
                              ₹{formatPrice(offerPrice)}
                            </span>
                            {offerMrp > offerPrice && (
                              <>
                                <span className="purchase-offer-mrp">
                                  ₹{formatPrice(offerMrp)}
                                </span>
                                <span className="purchase-offer-savings">
                                  {Math.round(((offerMrp - offerPrice) / offerMrp) * 100)}% off
                                </span>
                              </>
                            )}
                          </div>
                          <span
                            className={`purchase-offer-stock ${
                              isOfferInStock ? "in-stock" : "out-of-stock"
                            }`}
                          >
                            {isOfferInStock ? "In Stock" : "Out of Stock"}
                          </span>
                        </div>

                        <div className="purchase-offer-seller-info">
                          <span>Sold by:</span>
                          <strong>{offerName}</strong>
                          {rating > 0 && (
                            <span className="purchase-offer-rating">
                              <Star size={11} fill="#f59e0b" color="#f59e0b" />
                              {rating.toFixed(1)}
                            </span>
                          )}
                        </div>

                        {(hasFreeDelivery || hasFastDelivery) && (
                          <div className="purchase-offer-delivery-info">
                            <Truck size={15} />
                            <span>
                              {hasFastDelivery
                                ? "Fast Delivery available"
                                : "Free Delivery"}
                            </span>
                          </div>
                        )}

                        <div className="purchase-offer-actions">
                          <button
                            type="button"
                            className="purchase-offer-add-btn"
                            disabled={!isOfferInStock || isAdding}
                            onClick={() => handleAddOtherSellerToCart(offer)}
                          >
                            <ShoppingCart size={15} />
                            {isAdding ? "Adding..." : "Add to Cart"}
                          </button>
                          {offerSlug && (
                            <button
                              type="button"
                              className="purchase-offer-store-btn"
                              onClick={() => {
                                setIsDrawerOpen(false);
                                navigate(`/seller/${offerSlug}`);
                              }}
                            >
                              Seller Store
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default ProductPurchaseCard;
